import {
  CheckInWeekHeader,
  Message,
  MessageInput,
  PendingBoMessage,
} from "../../layer06";
import {
  useCoachBoConversations,
  useScrollToLatestMessage,
} from "../../../common";
import { Box, Stack } from "@mui/material";
import { DateTime } from "luxon";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "utils/redux/hooks";
import {
  selectChatbotMessages,
  selectSendMessageStatus,
  selectThreadId,
  sendGeneralMessage,
  setChatbotMessages,
  setThreadId,
} from "app/containers/Chatbot/slice";

export const CheckInActivityDetailSection = () => {
  const dispatch = useAppDispatch();
  const messages = useAppSelector(selectChatbotMessages);
  const threadId = useAppSelector(selectThreadId);
  const sendMessageStatus = useAppSelector(selectSendMessageStatus);
  const { conversations: allConversations } = useCoachBoConversations();
  const scrollContainerRef = useScrollToLatestMessage(messages?.length ?? 0);

  const sortedConversations = [...allConversations].sort((a, b) => {
    const aTime = DateTime.fromISO(a.created_at.replace(" ", "T")).toMillis();
    const bTime = DateTime.fromISO(b.created_at.replace(" ", "T")).toMillis();
    return bTime - aTime;
  });

  const checkInConversations = sortedConversations.filter(
    (c) => !!c.weeklyCheckInLink
  );

  // When conversations load, if we don't have a check-in threadId active yet,
  // auto-load the most recent check-in conversation.
  useEffect(() => {
    if (checkInConversations.length === 0) return;
    const mostRecent = checkInConversations[0];
    const isCheckInThread = checkInConversations.some(
      (c) => c.threadId === threadId
    );
    if (!isCheckInThread) {
      dispatch(setThreadId(mostRecent.threadId));
      dispatch(setChatbotMessages(mostRecent.messages));
    }
  }, [checkInConversations.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoToMostRecent = () => {
    const mostRecent = checkInConversations[0];
    if (!mostRecent) return;
    dispatch(setThreadId(mostRecent.threadId));
    dispatch(setChatbotMessages(mostRecent.messages));
  };

  const isMostRecent =
    checkInConversations.length === 0 ||
    threadId === checkInConversations[0]?.threadId;

  const activeConversation = checkInConversations.find(
    (c) => c.threadId === threadId
  );

  const pastConversationNote = (
    <>
      You are unable to edit past check-in conversations.{" "}
      <span onClick={handleGoToMostRecent} className="underline cursor-pointer">
        Click here to go to your most recent check-in conversation.
      </span>
    </>
  );

  return (
    <Box
      display="flex"
      sx={{
        paddingX: {
          xs: ".5rem",
          sm: "1.5rem",
        },
        height: {
          xs: "calc(100vh - 6.5rem)",
          sm: "calc(100vh - 4.5rem)",
        },
        boxSizing: "border-box",
      }}
    >
      {/* MESSAGES */}
      <Box
        ref={scrollContainerRef}
        flex="1 1 0"
        minWidth={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          height: "100%",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            height: "3rem",
            marginBottom: "-3rem",
            zIndex: 2,
            pointerEvents: "none",
            background: (theme) =>
              `linear-gradient(to bottom, ${theme.palette.background.default}, ${theme.palette.background.default}00)`,
          }}
        />
        <Stack
          direction="column"
          paddingTop="3rem"
          paddingBottom="6rem"
          flex="1 1 0"
          rowGap="1rem"
        >
          <CheckInWeekHeader
            date={DateTime.fromISO(
              (
                activeConversation?.created_at ?? DateTime.now().toISO()
              ).replace(" ", "T")
            )}
          />
          {(messages ?? []).map((node, i) => (
            <Message
              key={`${node.role}-${i}`}
              role={node.role === "assistant" ? "BO" : "USER"}
              content={node.content}
              sentTimeStamp={DateTime.local()}
            />
          ))}
          <PendingBoMessage />
        </Stack>
        <MessageInput
          onSend={(message) => {
            if (sendMessageStatus === "loading") {
              return false;
            }
            dispatch(sendGeneralMessage({ message }));
          }}
          disabled={!isMostRecent}
          note={{
            variant: "warning",
            text: isMostRecent
              ? "Conversation is shared with the Manager"
              : pastConversationNote,
          }}
        />
      </Box>
    </Box>
  );
};
